<script setup lang="ts">
import { mdiClose } from "@mdi/js";
import { useSettings } from "~/store/settings";
import t from "~/utils/translate";
import { isMobile } from "~/utils/constants";

interface ModalProps {
	active: boolean;
	title?: string;
	contentPadding?: boolean;
	footerPadding?: boolean;
	transparent?: boolean;
	fixedHeight?: string;
	alwaysRendered?: boolean;
	fullHeight?: boolean;
	anchor?: "top" | "middle" | "bottom";
	size?: "small" | "medium" | "large";
	rootClass?: string | Record<string, boolean> | (string | Record<string, boolean>)[];
	contentClass?: string | Record<string, boolean> | (string | Record<string, boolean>)[];
	contentBackgroundClass?: string | Record<string, boolean> | (string | Record<string, boolean>)[];
	disableClose?: boolean;
	headerAction?: string | false;
	containerId?: string;
	settledModal?: boolean;
	showOverflow?: boolean;
	isFullscreen?: boolean;
	overlayStrength?: "light" | "medium" | "strong" | "none";
	headerColor?: string;
	footerClass?: string;
}
const props = withDefaults(defineProps<ModalProps>(), {
	active: false,
	title: "",
	fullHeight: false,
	alwaysRendered: false,
	contentPadding: true,
	footerPadding: true,
	anchor: "middle",
	headerAction: false,
	transparent: false,
	fixedHeight: undefined,
	size: "medium",
	rootClass: "",
	contentClass: "",
	contentBackgroundClass: "bg-light-200 dark:bg-dark-800",
	disableClose: false,
	settledModal: false,
	showOverflow: false,
	isFullscreen: false,
	overlayStrength: "medium",
	headerColor: "bg-dark-900",
	footerClass: "",
});

const settings = useSettings();
const reload = () => window.location.reload();

const emit = defineEmits<{
	(event: "set-active", active: boolean): void;
	(event: "header-action"): void;
	(event: "modal-mounted"): void;
	(event: "key-press", key: string): void;
}>();

const closeEvent = new CustomEvent("modal-close", {
	bubbles: true,
});

const handleClose = (e?: MouseEvent) => {
	if (e) e.preventDefault();
	if (props.disableClose) return;
	document.dispatchEvent(closeEvent);
	emit("set-active", false);
};

const sizes = {
	small: tw`w-full md:w-[345px]`,
	medium: tw`w-full md:w-[600px]`,
	large: tw`w-full md:w-[1080px]`,
};

const anchorClass = {
	top: tw`items-start`,
	middle: tw`items-start md:items-center`,
	bottom: tw`items-end`,
};

const overlayClass = computed(() => {
	switch (props.overlayStrength) {
		case "light":
			return "bg-dark-700/20";
		case "medium":
			return "bg-dark-700/40";
		case "strong":
			return "bg-dark-700/75";
		case "none":
			return "bg-transparent";
		default:
			return "bg-dark-700/40";
	}
});

const handleKeydown = (e: KeyboardEvent) => {
	if (e.key === "Escape") handleClose();
	emit("key-press", e.key);
};

onMounted(() => {
	document.addEventListener("keydown", handleKeydown);
	emit("modal-mounted");
});

onUnmounted(() => {
	document.removeEventListener("keydown", handleKeydown);
	document.body.classList.remove("scroll-lock");
	document.removeEventListener("mousedown", clickout);
});

const wrapper = ref();
const container = ref();

const onAfterEnter = () => {
	if (props.active) setTimeout(() => document.body.classList.add("scroll-lock"), 50);
	document.addEventListener("mousedown", clickout);
};

const clickout = (event: MouseEvent) => {
	if (event.target === wrapper.value) {
		handleClose(event);
	}
};

const onAfterLeave = () => {
	document.body.classList.remove("scroll-lock");
	document.removeEventListener("mousedown", clickout);
};
</script>

<template>
	<Teleport to="body">
		<Transition name="fade" @after-enter="onAfterEnter" @after-leave="onAfterLeave" appear>
			<template v-if="alwaysRendered || active">
				<div
					ref="wrapper"
					v-show="active"
					:class="[
						'fixed top-0 left-0 bottom-0 right-0 z-50 flex justify-center',
						overlayClass,
						settings.isNativeApp ? 'py-[10px]' : 'pb-[55px] pt-[60px] md:pt-[100px]',
						anchorClass[anchor],
						rootClass,
					]"
				>
					<div
						:id="containerId"
						ref="container"
						:class="[
							sizes[props.size],
							'relative z-10 drop-shadow-sm max-w-[95%] box-content rounded-md  scrollbar-hidden',
							fixedHeight || 'max-h-full',
							{ 'overflow-hidden overflow-y-scroll overflow-x-hidden': !showOverflow },
							{ 'overflow-visible h-full flex flex-col': showOverflow },
						]"
					>
						<!-- Header -->
						<div
							v-if="!transparent"
							class=""
							:class="[
								settledModal ? 'border-b dark:border-b-dark-500 border-none' : 'border-none',
								'sticky top-0 left-0 z-20 flex items-center w-full px-2 py-4 pr-12 text-base font-bold capitalize lg:p-4 h-14  text-light-50 rounded-tl-md rounded-tr-md',
								headerColor ? headerColor : 'bg-dark-900',
							]"
						>
							<span v-if="$slots.titleElement" class="h-full"> <slot name="titleElement" /></span><span v-else>{{ props.title }}</span>
							<slot name="icon">
								<UiIcon
									id="modal-close-btn"
									class="absolute w-6 h-6 cursor-pointer fill-light-50 top-4 right-4"
									:path="mdiClose"
									@click="handleClose()"
									v-if="!disableClose"
								/>
							</slot>
							<div v-if="headerAction" class="absolute -translate-y-1/2 right-12 top-1/2">
								<UiButton @click="emit('header-action')" :label="headerAction" variant="secondary" />
							</div>
						</div>
						<NuxtErrorBoundary>
							<template #error="{ error }">
								<div class="p-4 bg-light-200 dark:bg-dark-800">
									<p class="mb-4 font-bold">{{ t("betway-bet-error.100000.title") }}</p>
									<p class="text-xs">
										{{ error }}
									</p>
								</div>
								<div class="flex justify-between w-full p-4 rounded-bl rounded-br bg-light-50 dark:bg-dark-900">
									<UiButton variant="secondary" label="Cancel" class="w-[48%]" @click="handleClose" />
									<UiButton label="Try Again" class="w-[48%]" @click="reload" />
								</div>
							</template>
							<div class="relative z-10" :class="[{ 'min-h-0': showOverflow }]">
								<!-- Overlay -->
								<slot v-if="$slots.overlay" name="overlay" />
								<div :class="['relative rounded-b-md', { 'flex flex-col h-full': showOverflow }]">
									<!-- Content -->
									<div
										v-if="$slots.default"
										:class="[
											contentClass,
											contentBackgroundClass,
											'relative text-dark-800 dark:text-light-50 overflow-hidden ',
											{
												'p-4': contentPadding,
												'h-[calc(80vh-78px)]': fullHeight && !settings.isNativeApp,
												'h-[calc(100vh-78px)]': fullHeight && settings.isNativeApp,
												'rounded-bl rounded-br': !$slots.footer,
												'overflow-y-scroll scrollbar-hidden': showOverflow,
											},
										]"
									>
										<div v-if="alwaysRendered" v-show="active"><slot /></div>
										<slot v-else-if="active" />
									</div>
									<!-- Footer -->
									<div
										v-if="$slots.footer && !transparent"
										:class="[
											'sticky bottom-0 left-0 z-10 flex flex-col items-center justify-center rounded-b-md dark:text-light-50',
											transparent ? 'bg-transparent' : 'bg-light-50 dark:bg-dark-700',
											settledModal ? ' border-t border-none dark:border-t-dark-500 shadow-[0_-8px_16px_0_rgba(0,0,0,0.16)]' : 'border-none',
											footerPadding ? 'p-4' : '',
											footerClass,
										]"
									>
										<slot name="footer" />
									</div>
								</div>
							</div>
						</NuxtErrorBoundary>
					</div>
				</div>
			</template>
		</Transition>
	</Teleport>
</template>

<style scoped>
.fade-enter-active,
.fade-leave-active {
	transition: opacity 0.15s ease;
}

.fade-enter-from,
.fade-leave-to {
	opacity: 0;
}
</style>
